import {
  UseQueryOptions,
  useQuery,
  UseMutationOptions,
  useMutation,
}                           from 'react-query';
import { AxiosError }       from 'axios';
import {
  callApi,
  requestAttachments,
}                           from '@utils/apiCaller';
import { Notification }     from '@components/Notification/Notification';
import { showErrorMessage } from '@utils/commonFunctions';
import {
  IInvoiceStatistics,
  IInvoiceDetails,
  IInvoiceHistory,
  ITax,
  ISearchInvoice,
  ITaxesValues,
  ITaxesResponse,
  IBulkCuidsFees,
}                           from '@typings/invoiceManagement';
import {
  ISearchParams,
  ISearchResult,
}                           from '@typings/search';
import { IFeeValues }       from '@typings/accountManagement';

// #region FETCH STATISTICS
const fetchInvoiceStats = async (): Promise<IInvoiceStatistics> => {
  try {
    const { data } = await callApi<IInvoiceStatistics>('intraInvoice/widget');

    return data;
  } catch (error) {
    Notification.enqueueSnackbar(
      showErrorMessage(error),
      'error',
    );

    throw error;
  }
};

export const useFetchInvoiceStats = (
  config?: UseQueryOptions<IInvoiceStatistics, AxiosError>,
) => useQuery(
  ['invoice-statistics'],
  fetchInvoiceStats,
  {
    retry                : false,
    refetchOnWindowFocus : false,
    ...config,
  },
);
// #endregion

// #region FETCH INVOICES
const fetchInvoices = async (payload: ISearchParams): Promise<ISearchResult<ISearchInvoice>> => {
  try {
    const { data = {} as ISearchResult<ISearchInvoice> } = await callApi(
      'intraInvoice/search',
      'post',
      payload,
    );

    return data;
  } catch (error) {
    Notification.enqueueSnackbar(
      showErrorMessage(error),
      'error',
    );

    throw error;
  }
};

export const useFetchInvoices = (
  payload : ISearchParams,
  config? : UseQueryOptions<ISearchResult<ISearchInvoice>, AxiosError>,
) => useQuery(
  ['invoices-list', payload],
  () => fetchInvoices(payload),
  {
    retry                : false,
    refetchOnWindowFocus : false,
    ...config,
  },
);
// #endregion

// #region FETCH INVOICE BY ID
export interface IInvoiceByIdPayload {
  id   : string;
  cuid : string;
}

const fetchInvoiceById = async ({ id, cuid }: IInvoiceByIdPayload): Promise<IInvoiceDetails> => {
  try {
    const { data } = await callApi<IInvoiceDetails>(`intraInvoice/${cuid}/${id}`);

    return data;
  } catch (error) {
    Notification.enqueueSnackbar(
      showErrorMessage(error),
      'error',
    );

    throw error;
  }
};

export const useFetchInvoiceById = (
  payload : IInvoiceByIdPayload,
  config  : UseQueryOptions<IInvoiceDetails, Error>,
) => useQuery(
  ['fetch-invoice-by-id', payload],
  () => fetchInvoiceById(payload),
  {
    retry                : false,
    refetchOnWindowFocus : false,
    ...config,
  },
);
// #endregion

// #region UPDATE INVOICE
const updateInvoice = async ({
  id,
  cuid,
  invoiceNumber,
  invoiceDate,
  total,
  ...updatedValues
}: IInvoiceDetails): Promise<IInvoiceDetails> => {
  try {
    const { data } = await callApi<IInvoiceDetails>(`intraInvoice/${cuid}/${id}`, 'put', updatedValues);

    Notification.enqueueSnackbar(
      'Invoice has been updated.',
      'info',
    );

    return data;
  } catch (error) {
    Notification.enqueueSnackbar(
      showErrorMessage(error),
      'error',
    );

    throw error;
  }
};

export const useUpdateInvoice = (
  config: UseMutationOptions<IInvoiceDetails, AxiosError, IInvoiceDetails> = {},
) => useMutation(
  updateInvoice,
  config,
);
// #endregion

// #region DOWNLOAD PDF INVOICE
interface IDownloadInvoicePayload {
  id       : string;
  cuid     : string;
  fileName : string;
}

const downloadPdfInvoice = async ({
  id,
  cuid,
}: IDownloadInvoicePayload): Promise<void> => {
  try {
    await requestAttachments(
      `pdf/invoice/${cuid}/${id}`,
      'pdf',
    );
  } catch (error) {
    Notification.enqueueSnackbar(
      showErrorMessage(error),
      'error',
    );

    throw error;
  }
};

export const useDownloadPdfInvoice = (
  config: UseMutationOptions<void, AxiosError, IDownloadInvoicePayload> = {},
) => useMutation(
  downloadPdfInvoice,
  config,
);
// #endregion

// #regiuon FETCH INVOICE HISTORY
const fetchInvoiceHistory = async (): Promise<IInvoiceHistory[]> => {
  try {
    const { data } = await callApi<IInvoiceHistory[]>('intraInvoice/history');

    return data;
  } catch (error) {
    Notification.enqueueSnackbar(
      showErrorMessage(error),
      'error',
    );

    throw error;
  }
};

export const useFetchInvoiceHistory = (
  config?: UseQueryOptions<IInvoiceHistory[], AxiosError>,
) => useQuery(
  ['invoice-history'],
  fetchInvoiceHistory,
  {
    retry                : false,
    refetchOnWindowFocus : false,
    ...config,
  },
);
// #endregion

// #region FETCH PRICING
const fetchPricing = async (): Promise<IFeeValues[]> => {
  try {
    const { data } = await callApi<IFeeValues[]>('intraInvoice/prices');
    return data;
  } catch (error) {
    Notification.enqueueSnackbar(
      showErrorMessage(error),
      'error',
    );

    throw error;
  }
};

export const useFetchPricing = (
  config?: UseQueryOptions<IFeeValues[], AxiosError>,
) => useQuery(
  ['default-pricing'],
  fetchPricing,
  {
    retry                : false,
    refetchOnWindowFocus : false,
    ...config,
  },
);
// #endregion

// #region UPDATE PRICING
const updatePricing = async (payload: IFeeValues[]): Promise<IFeeValues[]> => {
  try {
    const { data } = await callApi<IFeeValues[]>('intraInvoice/prices', 'put', payload);

    Notification.enqueueSnackbar(
      'Prices have been changed.',
      'info',
    );

    return data;
  } catch (error) {
    Notification.enqueueSnackbar(
      showErrorMessage(error),
      'error',
    );

    throw error;
  }
};

export const useUpdatePricing = (
  config: UseMutationOptions<IFeeValues[], AxiosError, IFeeValues[]> = {},
) => useMutation(
  updatePricing,
  config,
);
// #endregion

// #region FETCH TAXES
const fetchTaxes = async (): Promise<ITax[]> => {
  try {
    const { data } = await callApi<ITaxesResponse>('v2/taxes');

    return data.taxes;
  } catch (error) {
    Notification.enqueueSnackbar(
      showErrorMessage(error),
      'error',
    );

    throw error;
  }
};

export const useFetchTaxes = (
  config?: UseQueryOptions<ITax[], AxiosError>,
) => useQuery(
  ['taxes'],
  fetchTaxes,
  {
    retry                : false,
    refetchOnWindowFocus : false,
    ...config,
  },
);
// #endregion

// #region UPDATE TAXES
const updateTaxes = async (values: ITaxesValues): Promise<ITax[]> => {
  try {
    const { data } = await callApi<ITax[]>('v2/taxes', 'put', values);

    Notification.enqueueSnackbar(
      'Taxes have been changed.',
      'info',
    );

    return data;
  } catch (error) {
    Notification.enqueueSnackbar(
      showErrorMessage(error),
      'error',
    );

    throw error;
  }
};

export const useUpdateTaxes = (
  config: UseMutationOptions<ITax[], AxiosError, ITaxesValues> = {},
) => useMutation(
  updateTaxes,
  config,
);
// #endregion

// #region UPDATE BULK FEES
const updateBulkFees = async (values: IBulkCuidsFees): Promise<IBulkCuidsFees> => {
  try {
    const { data } = await callApi<IBulkCuidsFees>('v2/credit-unions/bulk/fees', 'put', values);

    Notification.enqueueSnackbar(
      'Fees have been changed.',
      'info',
    );

    return data;
  } catch (error) {
    Notification.enqueueSnackbar(
      showErrorMessage(error),
      'error',
    );

    throw error;
  }
};

export const useUpdateBulkFees = (
  config: UseMutationOptions<IBulkCuidsFees, AxiosError, IBulkCuidsFees> = {},
) => useMutation(
  updateBulkFees,
  config,
);
// #endregion
